// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { Table } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { DeleteButton } from '@shared/ui-kit/components/delete-button';
import { useTranslation } from 'react-i18next';
import { type Screener } from '@shared/data-access/interfaces/redux/my-research-state';
import { type FC, useMemo } from 'react';

export interface ScreenersTableProps {
  readonly data: Screener[];
  readonly onDeleteScreener: (universe_id: number, nameScreener: string) => void;
  readonly onLoadScreener: (universeId: number) => Promise<void>;
  readonly onDownloadScreener: (screener: Screener[]) => void;
}

const columnHelper = createColumnHelper<Screener>();

export const ScreenersTable: FC<ScreenersTableProps> = ({
  data,
  onDeleteScreener,
  onLoadScreener,
  onDownloadScreener,
}) => {
  const { t } = useTranslation();
  const columns = useMemo(
    () => [
      columnHelper.accessor('description', {
        header: t('common.name', { ns: 'common' }),
        size: 500,
        cell: info => (
          <span className="font-medium text-primary">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('creation_date', {
        header: t('common.date', { ns: 'common' }),
        size: 500,
        cell: info => <Table.Data value={info.getValue()} format="date" />,
      }),
      columnHelper.display({
        id: 'options',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex w-full items-center justify-end gap-x-4">
            <Tooltip title={t('research.download-screener', { ns: 'research' })}>
              <IconButton
                icon="Download"
                variant="fill"
                className="!bg-primary hover:!bg-opacity-50"
                onClick={() => onDownloadScreener([row.original])}
                data-testid={`download-screener-${row.original.universe_id}`}
              />
            </Tooltip>
            <Tooltip title={t('research.load-screener', { ns: 'research' })}>
              <IconButton
                data-testid="load-screener"
                onClick={() => onLoadScreener(row.original.universe_id)}
                icon="Download"
                variant="fill"
                className="rotate-180 !bg-primary hover:!bg-opacity-50"
              />
            </Tooltip>
            <DeleteButton
              data-testid="delete-screener"
              onClick={() =>
                onDeleteScreener(row.original.universe_id, row.original.description)
              }
            />
          </div>
        ),
      }),
    ],
    [onDeleteScreener, onDownloadScreener, onLoadScreener, t],
  );
  return <Table.Grouped columns={columns} data={data} />;
};
